import { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
	Download,
	Upload,
	Loader2,
	AlertTriangle,
	FileArchive,
	CheckCircle2,
} from "lucide-react";

import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Mensagens do backend traduzidas para o que a pessoa precisa fazer a
// respeito. Sem isto, "ERR_BACKUP_WRONG_PASSWORD" chegaria cru na tela.
const RECADOS = {
	ERR_BACKUP_WRONG_PASSWORD:
		"Senha incorreta para este arquivo. É a senha de quem gerou o backup, não necessariamente a sua.",
	ERR_BACKUP_INVALID_FILE:
		"Este arquivo não é um backup do sistema, ou está corrompido.",
	ERR_BACKUP_VERSION_UNSUPPORTED:
		"Este backup foi gerado por uma versão mais nova do sistema.",
	ERR_BACKUP_PASSWORD_REQUIRED: "Informe a senha.",
	ERR_BACKUP_PASSWORD_TOO_SHORT:
		"A senha do arquivo precisa ter pelo menos 12 caracteres.",
	ERR_BACKUP_PASSWORD_TOO_WEAK:
		"Senha fraca. Use pelo menos 12 caracteres misturando maiúsculas, minúsculas, números ou símbolos — ou uma frase de 20 caracteres.",
	ERR_BACKUP_TOO_LARGE: "Este arquivo é grande demais para ser um backup.",
	ERR_BACKUP_TOO_MANY_ATTEMPTS:
		"Muitas tentativas seguidas. Espere alguns minutos e tente de novo.",
	ERR_BACKUP_FILE_REQUIRED: "Escolha o arquivo do backup.",
	ERR_INVALID_CREDENTIALS: "Senha incorreta.",
};

// Mesma regra do servidor (`helpers/BackupSenha.ts`). Conferir aqui evita a
// pessoa esperar o ZIP inteiro ser gerado para só então ouvir "senha fraca".
const MINIMO_DA_SENHA = 12;
const TAMANHO_DE_FRASE = 20;

const problemaNaSenha = (senha) => {
	if (senha.length < MINIMO_DA_SENHA) {
		return `A senha do arquivo precisa ter pelo menos ${MINIMO_DA_SENHA} caracteres.`;
	}

	if (senha.length >= TAMANHO_DE_FRASE) return null;

	const tipos = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((t) =>
		t.test(senha),
	).length;

	if (tipos < 3) {
		return "Senha fraca. Misture maiúsculas, minúsculas, números ou símbolos — ou use uma frase de 20 caracteres.";
	}

	return null;
};

// Gerador de senha forte, para quem não quer inventar uma.
//
// `crypto.getRandomValues` e não `Math.random`: o segundo é previsível, e uma
// senha previsível não protege arquivo nenhum.
const sortearSenha = () => {
	const alfabeto =
		"abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#%&*+-=";
	const bytes = new Uint32Array(24);
	crypto.getRandomValues(bytes);

	return Array.from(bytes, (n) => alfabeto[n % alfabeto.length]).join("");
};

const recado = (err) => {
	const chave = err?.response?.data?.error;
	return RECADOS[chave] || null;
};

const Bloco = ({ icon: Icon, title, description, children }) => (
	<div className="overflow-hidden rounded-xl border bg-card">
		<div className="flex items-start gap-3 border-b px-5 py-4">
			<span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
				<Icon className="h-4 w-4" />
			</span>
			<div className="min-w-0">
				<h2 className="text-sm font-semibold tracking-tight">{title}</h2>
				<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
					{description}
				</p>
			</div>
		</div>
		<div className="space-y-4 px-5 py-5">{children}</div>
	</div>
);

const BackupRestore = () => {
	const [senhaExport, setSenhaExport] = useState("");
	const [repetirSenha, setRepetirSenha] = useState("");
	const [exportando, setExportando] = useState(false);

	const [arquivo, setArquivo] = useState(null);
	const [senhaImport, setSenhaImport] = useState("");
	const [modo, setModo] = useState("substituir");
	const [importando, setImportando] = useState(false);
	const [confirmando, setConfirmando] = useState(false);
	const [resultado, setResultado] = useState(null);

	const inputArquivo = useRef(null);

	const exportar = async () => {
		// Confere aqui em vez de deixar o servidor recusar: senha de arquivo
		// não tem "esqueci" — digitada errada, o backup fica ilegível para
		// sempre, e ninguém descobre no dia em que for restaurar.
		const problema = problemaNaSenha(senhaExport);

		if (problema) {
			toast.error(problema);
			return;
		}

		if (senhaExport !== repetirSenha) {
			toast.error("As duas senhas não são iguais.");
			return;
		}

		setExportando(true);
		try {
			// `blob`: a resposta é um ZIP, não JSON. Sem isto o axios tenta
			// interpretar como texto e o arquivo baixado chega corrompido.
			const { data, headers } = await api.post(
				"/backup/export",
				{ password: senhaExport },
				{ responseType: "blob" },
			);

			const nome =
				(headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] ||
				"backup.zip";

			const url = window.URL.createObjectURL(new Blob([data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = nome;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			setSenhaExport("");
			setRepetirSenha("");
			toast.success("Backup gerado. O download começou.");
		} catch (err) {
			// Com responseType blob, a mensagem de erro também vem como blob.
			if (err?.response?.data instanceof Blob) {
				try {
					const texto = await err.response.data.text();
					err.response.data = JSON.parse(texto);
				} catch {
					// Fica com o erro genérico mesmo.
				}
			}

			const aviso = recado(err);
			if (aviso) toast.error(aviso);
			else toastError(err);
		}
		setExportando(false);
	};

	const importar = async () => {
		setConfirmando(false);
		setImportando(true);
		setResultado(null);

		const formData = new FormData();
		formData.append("file", arquivo);
		formData.append("password", senhaImport);
		formData.append("mode", modo);

		try {
			const { data } = await api.post("/backup/import", formData);

			setResultado(data);
			setArquivo(null);
			setSenhaImport("");
			if (inputArquivo.current) inputArquivo.current.value = "";
			toast.success("Backup restaurado.");
		} catch (err) {
			const aviso = recado(err);
			if (aviso) toast.error(aviso);
			else toastError(err);
		}
		setImportando(false);
	};

	const totalDe = (mapa) =>
		Object.values(mapa || {}).reduce((soma, n) => soma + n, 0);

	return (
		<div className="space-y-4">
			<Bloco
				icon={Download}
				title="Exportar backup"
				description="Gera um arquivo ZIP com a configuração da empresa: filas, usuários, conexões, contatos, etiquetas, respostas rápidas, chatbot, fluxos, campanhas, CRM e as mídias usadas por eles."
			>
				<div className="flex gap-2 rounded-lg bg-muted p-3 text-xs leading-relaxed">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
					<div>
						Você <strong>escolhe uma senha para este arquivo</strong> — não é a
						do seu login. Guarde-a num gerenciador de senhas: sem ela o backup
						não abre, e não existe como recuperar o conteúdo depois.
						<br />
						O arquivo leva o <strong>hash de senha de todo mundo</strong>, o
						token de cada conexão e as chaves de API. É essa senha que o
						protege se ele parar na mão errada — use uma longa.
						<br />
						Atendimentos e mensagens <strong>não</strong> entram no backup, e
						cada conexão do WhatsApp precisará de um QR Code novo depois de
						restaurar.
					</div>
				</div>

				<div className="flex flex-col gap-3 sm:flex-row sm:items-end">
					<div className="flex-1">
						<div className="flex items-center justify-between gap-2">
							<Label htmlFor="senha-backup">Crie uma senha para o arquivo</Label>
							<button
								type="button"
								className="text-xs font-medium text-primary hover:underline"
								onClick={() => {
									const nova = sortearSenha();
									setSenhaExport(nova);
									setRepetirSenha(nova);
									navigator.clipboard?.writeText(nova).then(
										() =>
											toast.success(
												"Senha forte gerada e copiada. Guarde-a antes de exportar.",
											),
										() =>
											toast.success(
												"Senha forte gerada. Copie-a antes de exportar.",
											),
									);
								}}
							>
								Gerar senha forte
							</button>
						</div>
						{/* `new-password`: sem isto o navegador preenche sozinho a senha
						    salva do login, e a pessoa gera o backup com uma senha que
						    não sabe qual é. */}
						<Input
							id="senha-backup"
							type="password"
							autoComplete="new-password"
							value={senhaExport}
							onChange={(e) => setSenhaExport(e.target.value)}
							placeholder="Mínimo de 12 caracteres"
						/>
					</div>
					<div className="flex-1">
						<Label htmlFor="senha-backup-2">Repita a senha</Label>
						<Input
							id="senha-backup-2"
							type="password"
							autoComplete="new-password"
							value={repetirSenha}
							onChange={(e) => setRepetirSenha(e.target.value)}
							placeholder="A mesma de novo"
						/>
					</div>
					<Button
						onClick={exportar}
						disabled={exportando || !senhaExport || !repetirSenha}
					>
						{exportando ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Download className="h-4 w-4" />
						)}
						Exportar backup
					</Button>
				</div>
			</Bloco>

			<Bloco
				icon={Upload}
				title="Restaurar backup"
				description="Lê um arquivo gerado pela exportação e recria a configuração nesta empresa."
			>
				<div className="flex gap-2 rounded-lg bg-muted p-3 text-xs leading-relaxed">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
					<div>
						A senha pedida é a de <strong>quem gerou o arquivo</strong>, que pode
						ser outra pessoa e outra instalação.
						<br />
						Cadastros que já existem aqui e têm o mesmo identificador (usuário
						pelo e-mail, fila pelo nome, contato pelo número) são{" "}
						<strong>atualizados</strong> nos dois modos — nunca duplicados nem
						apagados. Sua própria conta nunca é alterada, para você não ficar
						trancado do lado de fora.
					</div>
				</div>

				<div>
					<Label htmlFor="arquivo-backup">Arquivo do backup (.zip)</Label>
					<Input
						id="arquivo-backup"
						ref={inputArquivo}
						type="file"
						accept=".zip,application/zip"
						onChange={(e) => setArquivo(e.target.files?.[0] || null)}
					/>
				</div>

				<div className="flex flex-col gap-3 sm:flex-row">
					<div className="flex-1">
						<Label htmlFor="senha-import">Senha do backup</Label>
						<Input
							id="senha-import"
							type="password"
							autoComplete="off"
							value={senhaImport}
							onChange={(e) => setSenhaImport(e.target.value)}
							placeholder="Senha de quem gerou o arquivo"
						/>
					</div>
					<div className="flex-1">
						<Label htmlFor="modo-import">O que fazer com o que já existe</Label>
						<Select value={modo} onValueChange={setModo}>
							<SelectTrigger id="modo-import">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="substituir">
									Substituir — apaga as listas atuais e põe as do backup
								</SelectItem>
								<SelectItem value="somar">
									Somar — mantém o que existe e acrescenta o do backup
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>

				<div className="flex justify-end">
					<Button
						variant={modo === "substituir" ? "destructive" : "default"}
						onClick={() => setConfirmando(true)}
						disabled={importando || !arquivo || !senhaImport}
					>
						{importando ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Upload className="h-4 w-4" />
						)}
						Restaurar
					</Button>
				</div>

				{resultado ? (
					<div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs leading-relaxed">
						<div className="flex items-center gap-2 font-medium text-emerald-600 dark:text-emerald-400">
							<CheckCircle2 className="h-4 w-4" />
							Restauração concluída
						</div>
						<p className="mt-1 text-muted-foreground">
							Origem: {resultado.origem?.companyName || "—"}
							{resultado.origem?.criadoEm
								? ` · gerado em ${new Date(
										resultado.origem.criadoEm,
									).toLocaleString("pt-BR")}`
								: ""}
						</p>
						<p className="mt-1 text-muted-foreground">
							{totalDe(resultado.criados)} registros criados,{" "}
							{totalDe(resultado.atualizados)} atualizados,{" "}
							{resultado.midias} arquivos de mídia.
						</p>

						{/* O que NÃO entrou é a informação que faltava. Antes a tela
						    dizia só "concluída", e a pessoa descobria semanas depois
						    que metade dos usuários não tinha sido restaurada. */}
						{totalDe(resultado.ignorados) > 0 ? (
							<p className="mt-1 font-medium text-amber-600 dark:text-amber-400">
								{totalDe(resultado.ignorados)} registro(s) do arquivo não
								couberam aqui:{" "}
								{Object.entries(resultado.ignorados || {})
									.filter(([, n]) => n > 0)
									.map(([chave, n]) => `${chave} (${n})`)
									.join(", ")}
								.
							</p>
						) : null}

						{resultado.avisos?.length ? (
							<ul className="mt-1 list-disc space-y-0.5 pl-4 text-amber-600 dark:text-amber-400">
								{resultado.avisos.map((aviso) => (
									<li key={aviso}>{aviso}</li>
								))}
							</ul>
						) : null}

						<p className="mt-1 text-muted-foreground">
							Reabra as telas para ver o resultado. Cada conexão do WhatsApp
							precisa de um QR Code novo.
						</p>
					</div>
				) : null}
			</Bloco>

			<Dialog open={confirmando} onOpenChange={setConfirmando}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<FileArchive className="h-4 w-4" />
							Restaurar este backup?
						</DialogTitle>
					</DialogHeader>

					<div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
						<p>
							Arquivo: <strong>{arquivo?.name}</strong>
						</p>
						{modo === "substituir" ? (
							<p>
								No modo <strong>substituir</strong>, as listas atuais desta
								empresa — chatbot, etapas de funil, faixas de quadro, avisos,
								campanhas, itens de mídia — são apagadas e recriadas a partir do
								backup. Não há como desfazer.
							</p>
						) : (
							<p>
								No modo <strong>somar</strong>, nada é apagado: o que vier do
								backup é acrescentado ao que já existe.
							</p>
						)}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setConfirmando(false)}>
							Cancelar
						</Button>
						<Button
							variant={modo === "substituir" ? "destructive" : "default"}
							onClick={importar}
						>
							Restaurar agora
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default BackupRestore;
